import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Shield, Gavel, ScrollText, AlertTriangle, CheckCircle2 } from "lucide-react";

const DISCORD_URL = "https://discord.gg/nEvwBUtqAV";

const SECTIONS = [
  {
    icon: ScrollText,
    title: "Tournament Rules",
    items: [
      "Matches are best-of-3 by default, with Grand Finals at best-of-5.",
      "All teams must check in 15 minutes before scheduled match time.",
      "Substitutions are allowed only before round 1 unless cleared by an admin.",
      "Disconnections over 2 minutes forfeit the current game.",
      "Screenshots of final scores must be submitted to an admin in Discord.",
    ],
  },
  {
    icon: Gavel,
    title: "Match Conduct",
    items: [
      "No stream-sniping, ghosting, or external voice help from non-participants.",
      "All third-party software must be approved in advance.",
      "Admins have full discretion to pause, void, or replay matches.",
      "Disputes must be raised in the ticket channel within 15 minutes of the match ending.",
    ],
  },
  {
    icon: Shield,
    title: "Community Guidelines",
    items: [
      "Respect every member — harassment, hate speech, or slurs get you banned.",
      "No doxxing, leaking DMs, or sharing personal info of any member.",
      "Keep channels on-topic; self-promotion only in #self-promo.",
      "English only in tournament channels to keep admin moderation consistent.",
      "Discord rules and ToS apply everywhere on the server.",
    ],
  },
  {
    icon: AlertTriangle,
    title: "Penalties",
    items: [
      "1st offense: verbal warning from a moderator.",
      "2nd offense: 24h mute and match forfeit if applicable.",
      "3rd offense: permanent ban and tournament disqualification.",
      "Cheating or account sharing = immediate permanent ban.",
    ],
  },
];

export default function RulesPage() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono">Official Handbook</div>
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mt-2">
            Rules &amp; Guidelines
          </h1>
          <p className="text-zinc-400 mt-3 max-w-2xl">
            Fair play makes great competition. Every Spicy Community member agrees to these rules on registration.
          </p>
        </div>

        <div className="space-y-5">
          {SECTIONS.map((s, i) => (
            <section
              key={i}
              data-testid={`rules-section-${i}`}
              className="glass-dark rounded-2xl p-6 md:p-8"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="h-10 w-10 rounded-full bg-gradient-to-b from-red-500/80 to-red-950/80 border border-red-400/40 flex items-center justify-center shadow-[inset_0_1px_0_rgba(255,255,255,0.3),0_0_16px_rgba(230,0,0,0.5)]">
                  <s.icon size={16} className="text-white" />
                </div>
                <h2 className="font-display font-bold text-2xl tracking-tight">{s.title}</h2>
              </div>
              <ul className="space-y-3">
                {s.items.map((it, j) => (
                  <li key={j} className="flex items-start gap-3 text-zinc-300">
                    <CheckCircle2 size={16} className="text-red-400 flex-shrink-0 mt-1" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="mt-10 glass-red-glow rounded-2xl p-6 md:p-8 text-center">
          <h3 className="font-display font-bold text-xl md:text-2xl mb-2">Questions about these rules?</h3>
          <p className="text-zinc-300 text-sm mb-5">Every admin is online in Discord to clarify anything.</p>
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="rules-ask-admin"
            className="btn-liquid inline-flex"
          >
            Ask on Discord
          </a>
        </div>
      </div>
      <Footer discordUrl={DISCORD_URL} />
    </div>
  );
}
